import { useState, FormEvent } from 'react';

const FORM_ENDPOINT = 'https://readdy.ai/api/form/dao9fpes79n0eakefdl0';

type ContactInfo = {
  icon: string;
  label: string;
  value: string;
  hint: string;
  href?: string;
  external?: boolean;
};

const infos: ContactInfo[] = [
  {
    icon: 'ri-map-pin-2-line',
    label: 'Adresse',
    value: 'Rabat, Maroc',
    hint: 'Siège de MEDICACLIC',
  },
  {
    icon: 'ri-mail-line',
    label: 'Email',
    value: 'contact@medicaclic.com',
    hint: 'Réponse sous 24 h ouvrées',
    href: 'mailto:contact@medicaclic.com',
  },
  {
    icon: 'ri-whatsapp-line',
    label: 'WhatsApp',
    value: 'Écrivez-nous sur WhatsApp',
    hint: 'Canal de confirmation prioritaire',
    href: 'https://wa.me/212612865005?text=Bonjour%2C%20je%20souhaite%20des%20informations%20via%20MedicaClic',
    external: true,
  },
  {
    icon: 'ri-time-line',
    label: 'Horaires',
    value: 'Réponse en semaine · 9h à 18h',
    hint: 'Écrivez-nous à tout moment, 7j/7',
  },
];

const subjects = [
  'Question générale',
  'Je suis patient',
  'Je suis praticien',
  'Partenariat / entreprise',
  'Presse & médias',
];

export default function Contact() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('contact_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      setFormError('');
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'contact_alt') return;
      if (typeof value === 'string' && value.trim() === '') return;
      payload.append(key, String(value));
    });

    setStatus('sending');
    setFormError('');

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (!isSpam && response.ok && parsed?.code === 'OK') {
        setStatus('success');
        form.reset();
        return;
      }

      setStatus('error');
      if (isSpam) {
        setFormError("Votre message n'a pas pu être envoyé. Merci de réessayer dans un instant.");
      } else if (serverMsg && !isSpam) {
        setFormError(String(serverMsg));
      } else {
        setFormError("Une erreur est survenue lors de l'envoi. Merci de réessayer.");
      }
    } catch {
      setStatus('error');
      setFormError("Impossible d'envoyer le message pour le moment. Vérifiez votre connexion et réessayez.");
    }
  };

  return (
    <section id="contact" className="py-20 md:py-28 bg-background-100 scroll-mt-20">
      <div className="px-4 md:px-8 lg:px-12 max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-primary-600 mb-3">
            Contact
          </span>
          <h2 className="font-heading text-3xl md:text-5xl font-semibold text-foreground-950 leading-tight">
            Une question, une demande ? <span className="italic text-primary-600">Parlons-en.</span>
          </h2>
          <p className="mt-4 text-sm md:text-base text-foreground-700 max-w-2xl mx-auto leading-relaxed">
            Patients, praticiens, partenaires : notre équipe basée à Rabat vous répond par email
            ou WhatsApp. Écrivez-nous, on s'occupe du reste.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {infos.map((info) => (
              <div
                key={info.label}
                className="rounded-xl bg-background-50 border border-background-200 p-5 flex flex-col gap-3"
              >
                <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
                  <i className={`${info.icon} text-xl w-5 h-5 flex items-center justify-center`} />
                </span>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-foreground-500 mb-1">
                    {info.label}
                  </p>
                  {info.href ? (
                    <a
                      href={info.href}
                      {...(info.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                      className="text-sm font-semibold text-primary-600 hover:text-primary-700 hover:underline transition-colors cursor-pointer break-all"
                    >
                      {info.value}
                    </a>
                  ) : (
                    <p className="text-sm font-semibold text-foreground-950">{info.value}</p>
                  )}
                  <p className="text-xs text-foreground-600 mt-1">{info.hint}</p>
                </div>
              </div>
            ))}

            <div className="sm:col-span-2 rounded-xl bg-primary-600 text-background-50 p-6 flex items-start gap-4">
              <span className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-background-50/15">
                <i className="ri-shield-check-line text-xl w-5 h-5 flex items-center justify-center" />
              </span>
              <div>
                <p className="text-sm font-semibold mb-1">Vos données sont protégées</p>
                <p className="text-xs text-background-100 leading-relaxed">
                  MEDICACLIC est conforme à la Loi 09-08 (CNDP). Aucune donnée n'est revendue
                  ni utilisée à des fins publicitaires.
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-xl bg-background-50 border border-background-200 p-6 md:p-8">
            {status === 'success' ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-10">
                <span className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
                  <i className="ri-check-line text-2xl w-6 h-6 flex items-center justify-center" />
                </span>
                <h3 className="font-heading text-xl font-semibold text-foreground-950 mb-2">
                  Message envoyé !
                </h3>
                <p className="text-sm text-foreground-700 max-w-sm">
                  Merci, nous avons bien reçu votre message. Notre équipe vous répond sous 24 h ouvrées.
                </p>
                <button
                  type="button"
                  onClick={() => setStatus('idle')}
                  className="mt-6 px-5 py-2.5 rounded-md bg-background-100 hover:bg-background-200 border border-background-300 text-foreground-800 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
                >
                  Envoyer un autre message
                </button>
              </div>
            ) : (
              <form data-readdy-form id="contact-form" onSubmit={handleSubmit} noValidate>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-xs font-semibold text-foreground-700 mb-1.5">
                      Nom complet
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      placeholder="Ex. Yasmine El Idrissi"
                      className="w-full px-4 py-2.5 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-xs font-semibold text-foreground-700 mb-1.5">
                      Email
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      placeholder="vous@exemple.com"
                      className="w-full px-4 py-2.5 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label htmlFor="contact-phone" className="block text-xs font-semibold text-foreground-700 mb-1.5">
                    Téléphone <span className="text-foreground-400 font-normal">(optionnel)</span>
                  </label>
                  <input
                    id="contact-phone"
                    name="phone"
                    type="tel"
                    placeholder="+212 6 00 00 00 00"
                    className="w-full px-4 py-2.5 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition"
                  />
                </div>

                <div className="mt-4">
                  <label htmlFor="contact-subject" className="block text-xs font-semibold text-foreground-700 mb-1.5">
                    Sujet
                  </label>
                  <select
                    id="contact-subject"
                    name="subject"
                    defaultValue={subjects[0]}
                    className="w-full px-4 py-2.5 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition cursor-pointer"
                  >
                    {subjects.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>

                <div className="mt-4">
                  <label htmlFor="contact-message" className="block text-xs font-semibold text-foreground-700 mb-1.5">
                    Votre message
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    rows={5}
                    required
                    maxLength={500}
                    placeholder="Décrivez votre demande en quelques lignes..."
                    className="w-full px-4 py-2.5 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition resize-none"
                  />
                  <p className="mt-1 text-xs text-foreground-400">500 caractères maximum.</p>
                </div>

                <input
                  type="text"
                  name="contact_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="mc-hp-field"
                />

                {status === 'error' && formError && (
                  <div className="mt-4 flex items-start gap-2 rounded-md bg-accent-100 border border-accent-300 px-4 py-3">
                    <i className="ri-error-warning-line text-accent-700 text-base w-4 h-4 flex items-center justify-center mt-0.5" />
                    <p className="text-xs text-accent-900 leading-relaxed">{formError}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === 'sending'}
                  className="mt-5 w-full px-6 py-3 rounded-md bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed text-background-50 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  {status === 'sending' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-base w-4 h-4 flex items-center justify-center" />
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line text-base w-4 h-4 flex items-center justify-center" />
                      Envoyer le message
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}